import React from 'react';
import {
  FEATURE_ALIGNMENT_CONTROL,
  FEATURE_BORDER_RADIUS_CONTROL,
  FEATURE_DIMENSIONS_CONTROL,
  FEATURE_OPACITY_CONTROL,
  FEATURE_PLAYBACKRATE_CONTROL,
  FEATURE_POSITION_CONTROL,
  FEATURE_ROTATION_CONTROL,
} from '../flags';
import { GifItem } from '../items/gif/gif-item-type';
import { AccordionHeader } from '@crabs-ai/ds-react';
import { AlignmentControls } from './controls/alignment-controls';
import { BorderRadiusControl } from './controls/border-radius-controls';
import { DimensionsControls } from './controls/dimensions-controls';
import { OpacityControls } from './controls/opacity-controls';
import { PlaybackRateControls } from './controls/playback-rate-controls';
import { PositionControl } from './controls/position-control';
import { RotationControl } from './controls/rotation-controls';
import { AnimationControls } from './controls/animation-controls';
import { EMPTY_ANIMATIONS } from '../items/animation/animation-item-type';
import { useTranslations } from 'next-intl';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';

const GIF_DEFAULTS = { positionShape: true, playbackRate: true };

const GifInspectorUnmemoized: React.FC<{
  item: GifItem;
  isMulti?: boolean;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}> = ({ item, collapseAllTrigger = 0, expandAllTrigger = 0 }) => {
  const t = useTranslations();
  const { openSections, toggleSection } = useInspectorSections({
    itemId: item.id,
    defaults: GIF_DEFAULTS,
    collapseAllTrigger,
    expandAllTrigger,
  });

  return (
    <div>
      <div className="flex flex-col gap-2xs">
        {/* Animation 섹션 */}
        <div className="border-b border-sys-style-neutral-light">
          <AnimationControls
            itemId={item.id}
            animations={item.animations ?? EMPTY_ANIMATIONS}
            durationInFrames={item.durationInFrames}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </div>
        {/*{FEATURE_SOURCE_CONTROL && !isMulti && <SourceControls item={item} />}*/}
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={'위치 및 모양'}
            isOpened={openSections.positionShape}
            onClick={() => toggleSection('positionShape')}
          />
          <CollapsibleContent isOpen={openSections.positionShape}>
            <div className="flex flex-col gap-4xs px-md py-4xs">
              {FEATURE_ALIGNMENT_CONTROL && (
                <AlignmentControls itemId={item.id} />
              )}
              {FEATURE_POSITION_CONTROL && <PositionControl itemId={item.id} />}
              {FEATURE_DIMENSIONS_CONTROL && (
                <DimensionsControls itemId={item.id} />
              )}
              {FEATURE_ROTATION_CONTROL && (
                <RotationControl
                  rotation={item.rotation}
                  itemId={item.id}
                />
              )}
              <div className={'flex w-full justify-between gap-4xs'}>
                {FEATURE_OPACITY_CONTROL && (
                  <div className={'w-full'}>
                    <OpacityControls
                      opacity={item.opacity}
                      itemId={item.id}
                    />
                  </div>
                )}
                {FEATURE_BORDER_RADIUS_CONTROL && (
                  <BorderRadiusControl
                    borderRadius={item.borderRadius}
                    itemId={item.id}
                  />
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
        {FEATURE_PLAYBACKRATE_CONTROL && (
          <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
            <AccordionHeader
              size="sm"
              title={t('Editor.inspector.playback-rate-control')}
              isOpened={openSections.playbackRate}
              onClick={() => toggleSection('playbackRate')}
            />
            <CollapsibleContent isOpen={openSections.playbackRate}>
              <div className={'px-md py-4xs'}>
                <PlaybackRateControls
                  playbackRate={item.playbackRate}
                  itemId={item.id}
                />
              </div>
            </CollapsibleContent>
          </div>
        )}
      </div>
    </div>
  );
};

export const GifInspector = React.memo(GifInspectorUnmemoized);
